import '@salesforce/canvas-js-sdk'
import axios from 'axios'
import { CANVAS_ACCESS_TOKEN_KEY } from '@labrav/common'
import { reportFrontendError } from '../../telemetry'
import { setTelemetryAuthType } from '../../store/telemetryContext'
import type {
  SalesforceRecord,
  SalesforceOrganization,
  CustomParameters,
  CanvasClient,
} from '../../externalApps/SalesforceApp/types/salesforce'
import { EXCHANGE_TOKEN_ABSENT_CLIENT } from '../../externalApps/SalesforceApp/utils/canvasAuthErrors'

type AxiosLikeError = Error & {
  response?: { status?: number; data?: unknown }
}

type CanvasAuthInitGate = {
  promise: Promise<void> | null
}

function getCanvasAuthInitGate(): CanvasAuthInitGate {
  const globalRef = globalThis as typeof globalThis & {
    __labraCanvasAuthInitGate?: CanvasAuthInitGate
  }
  if (!globalRef.__labraCanvasAuthInitGate) {
    globalRef.__labraCanvasAuthInitGate = { promise: null }
  }
  return globalRef.__labraCanvasAuthInitGate
}

/**
 * Canvas Authentication Service
 * Manages JWT tokens for Salesforce Canvas authentication
 * - Exchanges short-lived Exchange JWT for Access JWT
 * - Auto-refreshes tokens based on server-provided expiry time
 * - Stores tokens in memory only (no localStorage/sessionStorage)
 *
 * DEV MODE:
 * - When VITE_SALESFORCE_USE_AUTH0=true, bypasses Canvas backend
 * - Uses Auth0 token directly from auth context
 * - Skips token exchange and auto-refresh
 */
class CanvasAuthService {
  // Private fields (in-memory storage only)
  #accessToken: string | null = null
  #partnerId: string | null = null
  #crmId: string | null = null
  #refreshTimer: NodeJS.Timeout | null = null
  #isInitialized: boolean = false
  #initializePromise: Promise<void> | null = null
  #useAuth0: boolean = import.meta.env.VITE_SALESFORCE_USE_AUTH0 === 'true'
  #accessTokenExpirySeconds: number = 900 // Default: 15 minutes, updated from server response
  #record: SalesforceRecord | undefined = undefined
  #organization: SalesforceOrganization | null = null
  #locationUrl: string | null = null
  #canvasClient: CanvasClient | null = null
  #canvasId: string | null = null
  #customParameters: CustomParameters | null = null
  #scopes: string[] = []

  constructor() {
    // In dev mode (Auth0), initialize Salesforce context from environment variables
    // All fields are optional - set only what you need for testing
    if (this.#useAuth0) {
      // Load partnerId (optional)
      this.#partnerId = import.meta.env.VITE_SALESFORCE_DEV_PARTNER_ID || null

      // Load crmId (optional - for dev mode)
      this.#crmId = import.meta.env.VITE_SALESFORCE_DEV_CRM_ID || null

      // Load organization (optional)
      const orgId = import.meta.env.VITE_SALESFORCE_DEV_ORG_ID
      const instanceUrl = import.meta.env.VITE_SALESFORCE_DEV_INSTANCE_URL || ''
      if (orgId) {
        this.#organization = { Id: orgId }
      }

      // Set dev canvasClient with instanceUrl so getInstanceUrl() works in dev mode
      if (instanceUrl) {
        this.#canvasClient = {
          oauthToken: '',
          instanceId: '',
          instanceUrl,
          targetOrigin: '',
        }
      }

      // Load record (optional - only if both values provided)
      const recordId = import.meta.env.VITE_SALESFORCE_DEV_RECORD_ID
      const recordApiName = import.meta.env.VITE_SALESFORCE_DEV_RECORD_API_NAME
      if (recordId && recordApiName) {
        this.#record = { recordId, objectAPIName: recordApiName }
      }

      // Load locationUrl (optional - for postMessage testing)
      this.#locationUrl =
        import.meta.env.VITE_SALESFORCE_DEV_TARGET_ORIGIN || null
    }
  }

  /**
   * Initialize the auth service
   * - Exchanges Exchange JWT for Access JWT
   * - Starts auto-refresh timer based on token expiry
   *
   * Concurrent callers (React StrictMode remounts, duplicate module instances)
   * share one process-wide in-flight promise so /canvas/token is POSTed once.
   *
   * DEV MODE: Skips all steps if using Auth0
   */
  async initialize(): Promise<void> {
    if (this.#isInitialized) {
      return
    }

    const gate = getCanvasAuthInitGate()
    if (gate.promise) {
      await gate.promise
      return
    }
    if (this.#initializePromise) {
      return this.#initializePromise
    }

    const run = this.#initializeInternal().finally(() => {
      if (gate.promise === run) {
        gate.promise = null
      }
      if (this.#initializePromise === run) {
        this.#initializePromise = null
      }
    })
    gate.promise = run
    this.#initializePromise = run
    return run
  }

  async #initializeInternal(): Promise<void> {
    if (this.#isInitialized) {
      return
    }

    // DEV MODE: Skip Canvas backend initialization
    if (this.#useAuth0) {
      console.log('🔧 Dev Mode: Skipping Canvas authentication (using Auth0)')
      this.#scopes = [
        'aws:cosell:write',
        'aws:marketplace:write',
        'all:propensity:read',
      ]
      this.#isInitialized = true
      return
    }

    try {
      // Step 1: Exchange Exchange JWT for Access JWT
      await this.exchangeToken()

      // Step 2: Start auto-refresh timer based on token expiry
      this.startAutoRefresh()

      this.#isInitialized = true
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === EXCHANGE_TOKEN_ABSENT_CLIENT
      ) {
        throw error
      }
      throw new Error('Authentication initialization failed', { cause: error })
    }
  }

  /**
   * Get Exchange token from sessionStorage
   */
  getExchangeTokenFromSessionStorage(): string | null {
    return sessionStorage.getItem('exchange_token')
  }

  /**
   * Raw `signed_request` for CRM POST endpoints (e.g. event-bridge status).
   * Uses Salesforce Canvas `refreshSignedRequest`; not available in Auth0 dev mode.
   */
  getRawSignedRequestForCrmApi(): Promise<string> {
    if (this.#useAuth0) {
      return Promise.reject(
        new Error('Signed request is not available in Auth0 dev mode')
      )
    }
    return this.getRawSignedRequest()
  }

  /**
   * Get raw signed request string from Salesforce via refreshSignedRequest
   */
  private getRawSignedRequest(): Promise<string> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(
          new Error('Timed out waiting for Salesforce refreshSignedRequest')
        )
      }, 10_000)

      try {
        Sfdc.canvas.client.refreshSignedRequest(data => {
          clearTimeout(timeout)
          if (data.status === 200 && data.payload?.response) {
            resolve(data.payload.response)
          } else {
            reject(
              new Error(
                `refreshSignedRequest failed with status ${data.status}`
              )
            )
          }
        })
      } catch (err) {
        clearTimeout(timeout)
        reject(err)
      }
    })
  }

  /**
   * Decode oauthToken, instanceId, and refreshToken from a raw signed request payload
   * Note: Payload is decoded client-side for local use only.
   * Signature verification is performed by the backend when the signed request is sent to the refresh endpoint.
   */
  private decodeCanvasClientFromSignedRequest(rawSignedRequest: string): {
    oauthToken: string
    instanceId: string
    refreshToken?: string | null
  } {
    const parts = rawSignedRequest.split('.')
    const decoded = JSON.parse(atob(parts[1]))
    const oauthToken = decoded?.client?.oauthToken
    const instanceId = decoded?.client?.instanceId
    const refreshToken = decoded?.client?.refreshToken ?? null
    if (!oauthToken || !instanceId) {
      throw new Error('Missing oauthToken or instanceId in signed request')
    }
    return { oauthToken, instanceId, refreshToken }
  }

  /**
   * Exchange short-lived Exchange JWT for Access JWT (one-time use)
   */
  private async exchangeToken(): Promise<void> {
    const exchangeToken = this.getExchangeTokenFromSessionStorage()
    if (!exchangeToken) {
      const pathname =
        typeof window !== 'undefined' ? window.location.pathname : 'unknown'
      const absentError = new Error(EXCHANGE_TOKEN_ABSENT_CLIENT)
      console.error('❌ Exchange token absent in sessionStorage', { pathname })
      reportFrontendError('api.error', absentError, {
        'http.url': '/backend/salesforce/canvas/token',
        'http.method': 'POST',
        'http.status': 'client_short_circuit',
        'error.code': EXCHANGE_TOKEN_ABSENT_CLIENT,
        'http.pathname': pathname,
      })
      throw absentError
    }

    // Claim before await so a concurrent initialize (StrictMode / duplicate
    // singleton) cannot POST the same one-time JWT a second time.
    sessionStorage.removeItem('exchange_token')

    try {
      const response = await axios.post(
        '/backend/salesforce/canvas/token',
        {},
        {
          withCredentials: true,
          headers: {
            'X-Exchange-Token': exchangeToken,
          },
        }
      )

      this.#accessToken = response.data.accessToken
      this.persistCanvasToken()
      this.#partnerId = response.data.partnerId
      setTelemetryAuthType('salesforce-canvas')
      this.#crmId = response.data.crmId
      this.#accessTokenExpirySeconds = response.data.expiresIn || 900
      this.#record = response.data.record
      this.#organization = response.data.organization
      this.#locationUrl = response.data.locationUrl
      this.#canvasId = response.data.canvasId || null
      this.#customParameters = response.data.customParameters || null
      this.#scopes = Array.isArray(response.data.scopes)
        ? response.data.scopes.filter(
            (s: unknown): s is string => typeof s === 'string'
          )
        : []

      const serverClient = response.data.canvasClient
      if (serverClient) {
        // Set canvasClient immediately with instanceUrl from server so navigation
        // works right away, then populate oauthToken/instanceId/refreshToken in
        // the background via refreshSignedRequest (saves ~100-300ms from the
        // critical render path; viaSalesforce navigation falls back to window.open
        // during the brief window before the signed-request callback fires).
        this.#canvasClient = {
          ...serverClient,
          oauthToken: '',
          instanceId: '',
          refreshToken: null,
        }
        this.getRawSignedRequest()
          .then(rawSignedRequest => {
            const { oauthToken, instanceId, refreshToken } =
              this.decodeCanvasClientFromSignedRequest(rawSignedRequest)
            this.#canvasClient = {
              ...this.#canvasClient!,
              oauthToken,
              instanceId,
              refreshToken,
            }
          })
          .catch(() => {
            // canvasClient keeps instanceUrl for basic navigation; only
            // Sfdc.canvas.client.publish paths (viaSalesforce) are degraded.
          })
      } else {
        this.#canvasClient = null
      }
    } catch (error: unknown) {
      const err = error as AxiosLikeError
      // Restore only when the server did not clearly consume/reject the JWT,
      // so a later retry can reclaim it.
      if (!err.response || (err.response.status ?? 0) >= 500) {
        sessionStorage.setItem('exchange_token', exchangeToken)
      }
      console.error('❌ Token exchange failed:', { cause: error })
      reportFrontendError('api.error', err, {
        'http.url': '/backend/salesforce/canvas/token',
        'http.method': 'POST',
        'http.status': String(err.response?.status ?? 'network_error'),
        'http.response_body': JSON.stringify(err.response?.data ?? null),
      })
      if (err.response?.status === 401) {
        // Exchange JWT expired or invalid - redirect to re-authenticate
        this.handleSessionExpiry()
      }

      throw error
    }
  }

  /**
   * Refresh access token (get new Access JWT)
   * DEV MODE: No-op (Auth0 token refresh handled by Auth0)
   */
  async refreshToken(): Promise<void> {
    if (this.#useAuth0) return

    try {
      const rawSignedRequest = await this.getRawSignedRequest()

      const response = await axios.post(
        '/backend/salesforce/canvas/token/refresh',
        { signed_request: rawSignedRequest },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${this.#accessToken}`,
          },
        }
      )

      this.#accessToken = response.data.accessToken
      this.persistCanvasToken()
      this.#accessTokenExpirySeconds = response.data.expiresIn || 900
      this.#scopes = Array.isArray(response.data.scopes)
        ? response.data.scopes.filter(
            (s: unknown): s is string => typeof s === 'string'
          )
        : this.#scopes

      // Update canvasClient with fresh oauthToken/instanceId/refreshToken
      if (this.#canvasClient) {
        const { oauthToken, instanceId, refreshToken } =
          this.decodeCanvasClientFromSignedRequest(rawSignedRequest)
        this.#canvasClient = {
          ...this.#canvasClient,
          oauthToken,
          instanceId,
          refreshToken,
        }
      }

      // Notify listeners (e.g. SalesforceContext) that auth/scopes changed.
      window.dispatchEvent(new Event('canvas-auth-updated'))
    } catch (error: unknown) {
      const err = error as AxiosLikeError
      console.error('❌ Token refresh failed:', { cause: error })
      reportFrontendError('api.error', err, {
        'http.url': '/backend/salesforce/canvas/token/refresh',
        'http.method': 'POST',
        'http.status': String(err.response?.status ?? 'network_error'),
        'http.response_body': JSON.stringify(err.response?.data ?? null),
      })
      if (err.response?.status === 401) {
        this.handleSessionExpiry()
      }
      throw error
    }
  }

  /**
   * Get current access token
   * DEV MODE: Returns Auth0 token from localStorage
   */
  getAccessToken(): string | null {
    // DEV MODE: Return Auth0 token from localStorage
    if (this.#useAuth0) {
      try {
        return localStorage.getItem('auth0-access-token')
      } catch (error) {
        console.error('❌ Failed to get Auth0 token from localStorage:', error)
        return null
      }
    }

    return this.#accessToken
  }

  /**
   * Get partner ID
   */
  getPartnerId(): string | null {
    return this.#partnerId
  }

  /**
   * Get CRM ID
   */
  getCrmId(): string | null {
    return this.#crmId
  }

  /**
   * Get record information (optional - only available when launched from record context)
   */
  getRecord(): SalesforceRecord | undefined {
    return this.#record
  }

  /**
   * Get organization information
   */
  getOrganization(): SalesforceOrganization | null {
    return this.#organization
  }

  /**
   * Get location URL for secure postMessage communication
   */
  getLocationUrl(): string | null {
    return this.#locationUrl
  }

  /**
   * Get Salesforce instance URL for building navigation URLs
   */
  getInstanceUrl(): string | null {
    return this.#canvasClient?.instanceUrl ?? null
  }

  /**
   * Get Canvas client object for Canvas SDK communication
   */
  getCanvasClient(): CanvasClient | null {
    return this.#canvasClient
  }

  /**
   * Get canvas ID for secure postMessage validation
   */
  getCanvasId(): string | null {
    return this.#canvasId
  }

  /**
   * Get custom parameters passed from Salesforce Canvas
   */
  getCustomParameters(): CustomParameters | null {
    return this.#customParameters
  }

  /**
   * Get normalized authorization scopes from Canvas JWT exchange response.
   */
  getScopes(): string[] {
    return this.#scopes
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.#isInitialized
  }

  /**
   * Start auto-refresh timer
   * Refreshes 60 seconds before token expiry to ensure uninterrupted access
   */
  private startAutoRefresh(): void {
    // Clear existing timer if any
    if (this.#refreshTimer) {
      clearInterval(this.#refreshTimer)
    }

    // Refresh 60 seconds before token expiry (or at 80% of expiry time, whichever is sooner)
    const bufferSeconds = Math.min(
      60,
      Math.floor(this.#accessTokenExpirySeconds * 0.2)
    )
    const refreshInterval =
      (this.#accessTokenExpirySeconds - bufferSeconds) * 1000

    this.#refreshTimer = setInterval(async () => {
      try {
        await this.refreshToken()
      } catch (error) {
        console.error('❌ Auto-refresh failed:', error)
        this.stopAutoRefresh()
      }
    }, refreshInterval)
  }

  /**
   * Stop auto-refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.#refreshTimer) {
      clearInterval(this.#refreshTimer)
      this.#refreshTimer = null
    }
  }

  /**
   * Persist the current access token to localStorage so the shared
   * `@labrav/common` API interceptor (and any MFE bundling its own
   * uninitialized CanvasAuthService) can read it as a fallback.
   */
  private persistCanvasToken(): void {
    try {
      if (this.#accessToken) {
        localStorage.setItem(CANVAS_ACCESS_TOKEN_KEY, this.#accessToken)
      } else {
        localStorage.removeItem(CANVAS_ACCESS_TOKEN_KEY)
      }
    } catch {
      // localStorage unavailable — in-memory token still works for the host.
    }
  }

  /** Remove the persisted Canvas token from localStorage. */
  private clearCanvasToken(): void {
    try {
      localStorage.removeItem(CANVAS_ACCESS_TOKEN_KEY)
    } catch {
      // no-op
    }
  }

  /**
   * Handle session expiry
   */
  private handleSessionExpiry(): void {
    this.stopAutoRefresh()
    this.clearCanvasToken()
    this.#accessToken = null
    this.#partnerId = null
    this.#crmId = null
    this.#record = undefined
    this.#organization = null
    this.#locationUrl = null
    this.#canvasClient = null
    this.#canvasId = null
    this.#customParameters = null
    this.#scopes = []
    this.#isInitialized = false
  }

  /**
   * Logout (destroy session)
   * DEV MODE: No-op (Auth0 logout handled separately)
   */
  async logout(): Promise<void> {
    // DEV MODE: No logout needed with Auth0
    if (this.#useAuth0) {
      this.#isInitialized = false

      // Clear partner and account cache on logout
      try {
        const { clearAllPartnerCache } = await import(
          '../utils/partnerDataCache'
        )
        clearAllPartnerCache()
      } catch (err) {
        console.warn('Failed to clear partner cache on logout:', err)
      }

      return
    }

    await axios.post(
      '/backend/salesforce/canvas/logout',
      {},
      {
        withCredentials: true,
        headers: {},
      }
    )

    this.stopAutoRefresh()
    this.clearCanvasToken()
    this.#accessToken = null
    this.#partnerId = null
    this.#crmId = null
    this.#record = undefined
    this.#organization = null
    this.#locationUrl = null
    this.#canvasClient = null
    this.#canvasId = null
    this.#customParameters = null
    this.#scopes = []
    this.#isInitialized = false

    // Clear partner and account cache on logout
    try {
      const { clearAllPartnerCache } = await import('../utils/partnerDataCache')
      clearAllPartnerCache()
    } catch (err) {
      console.warn('Failed to clear partner cache on logout:', err)
    }
  }
}

// Export singleton instance
export const canvasAuthService = new CanvasAuthService()
